
import { useState } from "react";
import { 
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetClose
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import PrioritizationChart from "./PrioritizationChart";
import { type Feedback } from "@/types";

type PrioritizationPopupProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  feedback: Feedback[];
  companyId: string;
  defaultScoreMethod: string | null;
  onCompanyUpdate: () => Promise<void>;
};

export function PrioritizationPopup({ 
  open, 
  onOpenChange, 
  feedback, 
  companyId, 
  defaultScoreMethod,
  onCompanyUpdate
}: PrioritizationPopupProps) {
  const [scoreMethod, setScoreMethod] = useState<'value_effort' | 'rice'>(
    (defaultScoreMethod as 'value_effort' | 'rice') || 'value_effort'
  );
  const [isUpdatingCompany, setIsUpdatingCompany] = useState(false);

  const handleSetCompanyDefault = async () => {
    if (!companyId) return;

    try {
      setIsUpdatingCompany(true);

      const { error } = await supabase
        .from('companies')
        .update({ default_score_method: scoreMethod })
        .eq('id', companyId);

      if (error) {
        throw error;
      }

      toast.success(`Default scoring method set to ${scoreMethod === 'value_effort' ? 'Value/Effort' : 'RICE'}`);
      onCompanyUpdate();
    } catch (error: any) {
      console.error('Error updating company:', error);
      toast.error(error.message || "Failed to update company preference");
    } finally {
      setIsUpdatingCompany(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[90%] sm:max-w-3xl overflow-y-auto">
        <SheetHeader className="mb-4">
          <SheetTitle>Feedback Prioritization</SheetTitle>
          <SheetDescription>
            Visualize your feedback items by priority
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6">
          <div className="space-y-2">
            <Label>Scoring Method</Label>
            <RadioGroup 
              value={scoreMethod} 
              onValueChange={(value) => setScoreMethod(value as 'value_effort' | 'rice')}
              className="flex space-x-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="value_effort" id="popup_value_effort" />
                <Label htmlFor="popup_value_effort" className="cursor-pointer">Value/Effort</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="rice" id="popup_rice" />
                <Label htmlFor="popup_rice" className="cursor-pointer">RICE Score</Label>
              </div>
            </RadioGroup>
          </div>

          <div>
            <Button 
              variant="outline" 
              onClick={handleSetCompanyDefault}
              disabled={isUpdatingCompany}
            >
              {isUpdatingCompany ? "Saving..." : "Set as Company Default"}
            </Button>
          </div>

          <div className="h-[500px]">
            <PrioritizationChart 
              feedback={feedback} 
              scoreMethod={scoreMethod} 
            />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
